import React,{Component}from 'react';
import { Trans } from 'react-i18next';
import { wavingpotgraph, displayThreshold, wavingpotsvg } from '../../common/utils/macros';
import {animatePots, isValidScoreValue} from '../../common/utils/common'
import Slider from "react-slick";
import {connect} from 'react-redux'
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ManualScoringVersion } from '../../../constants/chapterConstants';
import flaggedProctoringCardCurve from '../../../assets/images/flaggedProctoringCardCurve.svg'
import '../totalscoredetails.css'

const isAiConsentRevoked = (module) => module?.aiConsentAccepted === 0
// A module can have AI-scoring consent declined yet still carry a genuinely completed manual
// evaluation score (evaluators aren't blocked by declined AI consent). In that case the score
// itself should still be shown instead of "NA" — only the informational consent banner (which
// uses isAiConsentRevoked directly) should keep reflecting that AI scoring specifically was skipped.
// A real score (number or numeric string, since the API isn't consistent about which it sends)
// is a reliable-enough signal on its own: the backend only ever puts one here once a score
// genuinely exists (an incomplete evaluation carries no `score` field at all, e.g.
// `manualScoreObj: { value: "Pending" }`) — this also makes the check work right after saving
// feedback, before the next full refetch updates other bookkeeping fields.
const hasCompletedManualScore = (module) => isValidScoreValue(module?.manualScoreObj?.score)
const shouldHideScoreForConsent = (module) => isAiConsentRevoked(module) && !hasCompletedManualScore(module)

class TotalScoreDetails extends Component {
    constructor(props){
        super(props)
        
        /**
         * keeping a track of manual and automatic module, while updating from feeback chapter,
         *  we only need to update the manual modules 
         */
        this.chapterList = this.props.chapterList.map(i=>i.value)
    }

    navigateToChapterHandler=(chapterid,moduleid)=>{
        if(chapterid){
            window.externalLinks[chapterid] =  moduleid
        document.querySelector("ul.chapterList li.nav-item a#pills-"+chapterid+"-tab").click()
        }
    }

    // Render AI and Manual Scores
    renderScore = ({header, score, maxScore}) => {
        return(
            <div className="threshold font-semibold">
                <span className="threshold-header font-regular manual-header-pot">{header ?? "Score"}: </span>
                <span className="manual-score-pot-value">{`${score}/`}</span>
                <span className="manual-score-pot-value">{maxScore}</span>           
            </div>
        )
    }

    // Render manual scores if they exist
    renderManualScores = (module) => {
        if (isValidScoreValue(module?.manualScoreObj?.score)) {
            return this.renderScore(module.manualScoreObj);
        }
        return null;
    }

    // Render automated scores based on version and availability
    renderAutomatedScores = (module) => {
        if (module?.manualScoringVersion === ManualScoringVersion.NORMALIZED) {
            if (module?.normalisedAutomatedScoreObj?.score) {
                return this.renderScore(module.normalisedAutomatedScoreObj);
            }
        } else if (module?.automatedScoreObj?.score) {
            return this.renderScore(module.automatedScoreObj);
        } else {
            return displayThreshold(module, "Threshold");
        }
    }

    renderPotScore = (module, chapterdetails, chapterConstants) => {
        return (
            <div className='d-flex'>
                <div className="scoreVal">
                    <span className="score">{Math.round(module.moduleScore)}</span>
                    <span className="max-score">
                        {module.scoreScale ? '/'+module.scoreScale : chapterConstants?.SCORE_UNIT_PERCENTILE}
                    </span>
                </div>
                <div className="potVal">
                    {wavingpotgraph(module.percent,chapterdetails.colorScale)}
                </div>
            </div>
        )
    }

    renderAiConsentRevokedModule = (chapterdetails) => {
        return (
            <React.Fragment>
                <img className="flaggedCard" src={flaggedProctoringCardCurve} width={20} height={20} alt="img"/>
                <div className="scoreBox" data-colorscale={JSON.stringify(chapterdetails.colorScale.details)}>
                    <span className="naLabel"><Trans>NA</Trans></span>
                </div>
            </React.Fragment>
        )
    }

    // Render Total Score module for elementIds OS_TotalScore and OS_ManualScoringPending
    renderTotalScoreModule = (module, chapterdetails, chapterConstants) => {
        // moduleScore/percent are automated-scoring fields — for an AI-consent-denied module the
        // backend leaves them at a sentinel (e.g. -13) since automated scoring never ran at all.
        // Derive the pot graph from the manual score instead so it's shown consistently, rather
        // than only by accident right after saving feedback (before the score fields get stale).
        const potScoreModule = (isAiConsentRevoked(module) && hasCompletedManualScore(module))
            ? {
                moduleScore: module.manualScoreObj.score,
                scoreScale: module.manualScoreObj.maxScore,
                percent: (module.manualScoreObj.score / module.manualScoreObj.maxScore) * 100,
            }
            : module
        return (
            <div className="scoreBox" data-colorscale={JSON.stringify(chapterdetails.colorScale.details)}>
                {/* Render pot score */}
                {potScoreModule.moduleScore >= 0 && this.renderPotScore(potScoreModule, chapterdetails, chapterConstants)}
                
                {/* Manual score display */}
                {this.renderManualScores(module)}
                
                {/* Automated score display */}
                {this.renderAutomatedScores(module)}

                {module?.manualScoringVersion && !module?.isEvaluationCompletedByAll &&
                    <div className='incomplete-evaluations'>
                        {chapterConstants?.INCOMPLETE_EVALUATIONS}
                    </div>        
                }
            </div>
        );
    }

    // Render other module types
    renderOtherModule = (module, chapterdetails) => {
        return (
            <>    
                <div className="it-cont">
                    <div className="icon">
                        <img src={require('../../../assets/images/'+module.elementID+'.svg')} alt="img"/>
                    </div>
                    <div className="text">
                        {chapterdetails.elementTextMapping[module.elementID]}
                    </div>
                </div>

                {/* Manual score display */}
                {this.renderManualScores(module)}
                
                {/* Automated score display */}
                {this.renderAutomatedScores(module)}
            </>
        );
    }

    // Determine which renderer to use based on module type
    renderModuleContent = (module, chapterdetails, chapterConstants) => {
        if (shouldHideScoreForConsent(module)) {
            return this.renderAiConsentRevokedModule(chapterdetails);
        }
        // Once a consent-denied module has a genuine manual score, show it the same way a normal
        // scored module would be shown — never the elementID-driven "Not completed" label, even
        // if elementID itself (e.g. OS_NotCompleted, reflecting AI/automated scoring status) hasn't
        // caught up yet, such as right after saving feedback before the next full refetch.
        if (module.elementID === 'OS_TotalScore' || module.elementID === 'OS_ManualScoringPending'
            || (isAiConsentRevoked(module) && hasCompletedManualScore(module))) {
            return this.renderTotalScoreModule(module, chapterdetails, chapterConstants);
        } else {
            return this.renderOtherModule(module, chapterdetails);
        }
    }

    render(){
        let chapterdetails = this.props.chapterdetails
        let totalModuleScores = this.props.totalModuleScores
        let chapterConstants = chapterdetails.chapterConstants
        let totalScoresRender=[]
        let settings = {
                dots: false,
                infinite: false,
                slidesToShow: 4.5
        }
        
        const showAiConsentBanner = totalModuleScores.some(isAiConsentRevoked)
        const allConsentRevoked = totalModuleScores.length > 0 && totalModuleScores.every(isAiConsentRevoked)
        const bannerMessage = allConsentRevoked ? chapterConstants.AI_CONSENT_DENIED : chapterConstants.AI_PARTIAL_CONSENT_DENIED
    

        totalModuleScores.forEach((module) => {
            const consentRevoked = shouldHideScoreForConsent(module)
            let chapterMappingArr = chapterdetails.linkingIcons['chapterMapping'][module.moduleId] 
                ? chapterdetails.linkingIcons['chapterMapping'][module.moduleId] 
                : []
            let mappedChapter=""
            
            if(!consentRevoked && chapterMappingArr.length && chapterMappingArr.indexOf('CandidateResponseChapter')!==-1 && this.chapterList.indexOf('CandidateResponseChapter')!=-1){
                mappedChapter = 'CandidateResponseChapter'
            } else if(!consentRevoked && chapterMappingArr.length && chapterMappingArr.indexOf('CandidateProfileChapter')!==-1 && this.chapterList.indexOf('CandidateProfileChapter')!=-1){
                mappedChapter = 'CandidateProfileChapter'
            }

            const hasLinkClass = !consentRevoked && chapterdetails.linkingIcons['chapterMapping'][module.moduleId] ? 'hasLink ' : ''
            const revokedClass = consentRevoked ? ' aiConsentRevoked' : ''

            totalScoresRender.push(
                <div 
                    onClick={consentRevoked ? undefined : () => this.navigateToChapterHandler(mappedChapter, module.moduleId)} 
                    key={module.moduleId} 
                    className={`${hasLinkClass}potBox${revokedClass}`} 
                    data-moduleid={module.moduleId}
                    data-chapterid={mappedChapter} 
                    data-scorescale={module.scoreScale || ''}
                >
                    <div className="modName" data-toggle="tooltip" data-placement="top" title={module.moduleName}>
                        {module.moduleName}
                    </div>
                    {this.renderModuleContent(module, chapterdetails, chapterConstants)}
                </div>
            )
        })
        
        return (
            <>
                {showAiConsentBanner && (
                    <div className="aiConsentBanner" role="status">
                        <img src={flaggedProctoringCardCurve} alt="" className="aiConsentBannerIcon" />
                        <span>{bannerMessage}</span>
                    </div>
                )}
                <div className="pot">
                    {wavingpotsvg()}
                    <Slider {...settings} className="d-none d-lg-block d-xl-block">{totalScoresRender}</Slider>
                    <div className="d-block d-sm-block d-md-block d-lg-none" style={{width:"100%"}}>
                        <div className="pots-mobile flexContainer">
                            {totalScoresRender}
                        </div>
                    </div>
                </div>
            </>
        )   
    } 
    
    componentDidMount(){
        this.boxAnimate();
    }
    
    componentDidUpdate(){
        this.boxAnimate();
    }
    
    boxAnimate=()=>{
        document.querySelectorAll(".box").forEach(function(item,key){
            let max_percent = item.querySelector(".percent").getAttribute('data-percent')
            animatePots(item, max_percent)
        })
    }
}

const mapStateToProps = state =>{
    return {
        totalModuleScores:state.custom.totalModuleScores
    }
}
export default connect(mapStateToProps)(TotalScoreDetails)
